var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { StoreAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums';
import { AvailabilityUtility, ProductUtility } from '@inter-ikea-kompis/utilities';
import { ProductRowLayoutEnum, ProductRowOptionalStateEnum, ProductRowProductLinkEnum, ProductRowProductMeasureEnum } from '@inter-ikea-kompis/component-product-row';
import { ProductRow } from '@inter-ikea-kompis/component-product-row';
import { SortListOptionsEnum } from '@inter-ikea-kompis/component-sort-list';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import ProductListSorting from '../../utilities/ProductListSorting.js';
import ProductListSprUtility from '../../utilities/ProductListSprUtility.js';
import ProductListSection from '../product-list-section/ProductListSection.js';
import ProductListTotalPrice from '../product-list-total-price/ProductListTotalPrice.js';
import ProductListStyle from './ProductListStyle.js';
/**
 * Used by components like WhatsIncluded to display a product list.
 *
 * @event add Triggered when the an add button is clicked.
 * @event remove Triggered when the a remove button is clicked.
 * @event quantity Triggered when the quantity is changed.
 * @event expand Triggered when included shopping products gets expanded.
 * @event collect Triggered when a product is collected.
 * @event productImageClick Triggered when the user clicks on a product image.
 * @event productNameClick Triggered when the user clicks on a product name.
 * @event modalClose Triggered when the modal has been closed
 * @event modalOpen Triggered when a modal has been opened. event.detail.type tells you the type
 */
export default class ProductList extends KompisElement {
    constructor() {
        super(...arguments);
        // A list of included products in SPR products. The ProductRow component will find the correct Product for each child of the SPR product.
        this.includedProducts = null;
        // A list of product item numbers that can be added.
        this.addable = null;
        // A list of product item numbers that can be removed.
        this.removable = null;
        // A list of sections.
        this.sections = null;
        // A list of store availability objects. The ProductRow component will find the correct Store Availability for each child of the SPR product.
        this.storeAvailabilities = null;
        // A list of zip availability objects. The ProductRow component will find the correct Zip Availability for each child of the SPR product.
        this.zipAvailabilities = null;
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Product measures.
        this.productMeasures = ProductRowProductMeasureEnum.none;
        // Recommended pattern for normal and kiosk mode, is eventOnly to open a ProductInformationCard in a Skapa sheet modal. If using productInformationPage, disable on kiosk.
        this.productLink = ProductRowProductLinkEnum.productInformationPage;
        // Show product id.
        this.showItemNo = false;
        // Show product package weight info.
        this.showWeight = false;
        // Enable to be able to edit the quantity.
        this.editableQuantity = false;
        // Show text describing the color and/or material of the product.
        this.showDesignText = false;
        // Show product package measures.
        this.showPackageMeasures = false;
        // Show status indicator on print. Has no effect if layout is set to ProductRowLayoutEnum.compactPrint.
        this.showStockStatusOnPrint = false;
        // A list of expanded shopping products.
        this.expanded = null;
        // Whether to show the collect checkbox or not.
        this.collectable = false;
        // A list of product ids that are collected.
        this.collected = null;
        // Sets the minimum quantity value.
        this.quantityMinValue = 0;
        // Stores.
        this.stores = null;
        // Selected store id.
        this.storeId = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // How the products should be sorted, can be used with SortList component
        this.sortBy = SortListOptionsEnum.default;
        // Locale is used to determine how to sort products on departments properly
        this.locale = null;
        // Visible modal
        this.visibleModal = null;
        // Skip listing label
        this.skipListingLabel = null;
        // Semantic heading level for the heading with the highest level in the product list.
        this.maxHeadingLevel = null;
        // Layout for product list. NOTE: ProductRowLayoutEnum.compactPrint should only be used during print. Make sure SPR's has their child articles in includedProducts, since SPR's are not visible in compactPrint.
        this.layout = null;
        // Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
        this.measurementSystem = null;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${ProductListStyle.getStyle({
            theme: this.theme,
            isCompactLayout: this.layout === ProductRowLayoutEnum.compactPrint
        })}
			</style>
			<div class="container">${this.getSkipButton()}${this.getList()}${this.getTotalPrice()}</div>
			<div
				class="skipListingFocusElement"
				@focusout="${(event) => {
            const target = event.currentTarget;
            if (target) {
                target.tabIndex = -1;
            }
        }}"
			></div>
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('kompis-product-row', ProductRow);
        this.customElements.define('kompis-product-list-section', ProductListSection);
        this.customElements.define('kompis-product-list-total-price', ProductListTotalPrice);
        super.connectedCallback();
    }
    hasTranslationsAndSettings() {
        return !!this.localizedInformation || !!(this.translations && this.settings);
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    /**
     * Skip Button when entering a carousel through tabbing.
     *
     * @returns Template.
     */
    getSkipButton() {
        if (!this.skipListingLabel) {
            return null;
        }
        const primaryVariant = 'primary';
        return html `
			<skapa-button
				class="skipButton"
				variant="${primaryVariant}"
				inverse
				@click="${this.onSkipButtonClick.bind(this)}"
				>${this.skipListingLabel}</skapa-button
			>
		`;
    }
    /**
     * Event handler.
     */
    onSkipButtonClick() {
        const focusElement = this.shadowRoot?.querySelector('.skipListingFocusElement');
        focusElement.tabIndex = 0;
        focusElement.focus();
    }
    getSectionHeaders() {
        if (this.layout !== ProductRowLayoutEnum.compactPrint) {
            return null;
        }
        const { quantityShort, productArticleNo, productSalesAisle, productSalesLocation, productListTotalPriceSummary, weight, price, article } = this.getTranslations();
        return html `
			<div class="compactListHeaders" aria-hidden="true">
				<h4 class="text--body-s">${quantityShort}</h4>
				<h4 class="text--body-s">${article}</h4>
				<h4 class="text--body-s location">
					<span>${productSalesAisle}</span><span class="divider"></span
					><span>${productSalesLocation}</span>
				</h4>
				<h4 class="text--body-s">${productArticleNo}</h4>
				<h4 class="text--body-s">${weight}</h4>
				<h4 class="text--body-s">${price}</h4>
				<h4 class="text--body-s">${productListTotalPriceSummary}</h4>
			</div>
		`;
    }
    /**
     * Returns HTML for a list of ShoppingProduct rows or sections if defined.
     *
     * @returns Template.
     */
    getList() {
        const sections = this.layout === ProductRowLayoutEnum.compactPrint
            ? this.getStoreAvailabilitySectionStructure()
            : this.getSectionStructure();
        if (sections.length > 0) {
            return html `${sections.map((section) => this.getSection(section))}`;
        }
        const products = this.layout === ProductRowLayoutEnum.compactPrint
            ? this.getOnlyAddedArticleShoppingProducts()
            : this.shoppingProducts;
        return html `
			${this.getA11yList(this.sortProducts(products).map((shoppingProduct) => this.getRow(shoppingProduct)))}
		`;
    }
    getStoreAvailabilitySectionStructure() {
        const { productListProductsSectionTitle } = this.getTranslations();
        const shoppingProducts = this.getOnlyAddedArticleShoppingProducts();
        const outOfStockProducts = [];
        const nonOutOfStockProducts = [];
        shoppingProducts.forEach((shoppingProduct) => {
            const availability = AvailabilityUtility.getStoreAvailabilityForProduct({
                product: shoppingProduct.product,
                storeAvailabilities: this.storeAvailabilities
            });
            if (!availability) {
                nonOutOfStockProducts.push(shoppingProduct);
                return;
            }
            const status = AvailabilityUtility.getStoreAvailabilityStatus([availability]);
            if (status === StoreAvailabilityProbabilityEnum.outOfStock) {
                outOfStockProducts.push(shoppingProduct);
                return;
            }
            nonOutOfStockProducts.push(shoppingProduct);
        });
        return [
            { label: this.getOutOfStockText(), shoppingProducts: outOfStockProducts },
            { label: productListProductsSectionTitle, shoppingProducts: nonOutOfStockProducts }
        ];
    }
    getAddedShoppingProducts(shoppingProducts) {
        return shoppingProducts.filter((shoppingProduct) => {
            if (!this.addable) {
                return true;
            }
            return !this.addable.includes(shoppingProduct.product.itemId);
        });
    }
    getOnlyAddedArticleShoppingProducts(shoppingProducts) {
        const correctShoppingProducts = shoppingProducts ?? this.shoppingProducts;
        const flattened = ProductListSprUtility.flattenSprsOrBundles(this.getAddedShoppingProducts(correctShoppingProducts), this.includedProducts ?? []);
        return flattened.reduce((acc, shoppingProduct) => {
            const { itemId } = shoppingProduct.product;
            const existingProduct = this.getExistingProduct(acc, itemId);
            if (existingProduct) {
                existingProduct.quantity += shoppingProduct.quantity;
            }
            else {
                acc.push(shoppingProduct);
            }
            return acc;
        }, []);
    }
    getExistingProduct(shoppingProducts, itemId) {
        return shoppingProducts.find((includedProduct) => itemId === includedProduct.product.itemId && includedProduct.product.valid);
    }
    getStore() {
        return this.stores?.find((store) => store.id === this.storeId) ?? null;
    }
    getOutOfStockText() {
        const { storeAvailabilityOutOfStock } = this.getTranslations();
        return storeAvailabilityOutOfStock.replace('{{store}}', this.getStore()?.name ?? '');
    }
    /**
     * Returns HTML for a section.
     *
     * @param section A product section.
     * @param section.label The label of the section.
     * @param section.shoppingProducts The shopping products that should be shown in the section.
     * @returns Template.
     */
    getSection(section) {
        if (section.shoppingProducts.length === 0) {
            return null;
        }
        const products = this.layout === ProductRowLayoutEnum.compactPrint
            ? this.getOnlyAddedArticleShoppingProducts(section.shoppingProducts)
            : section.shoppingProducts;
        return html `
			<kompis-product-list-section
				.theme="${this.theme}"
				.label="${section.label}"
				.layout="${this.layout}"
				.numberOfItems="${section.shoppingProducts.length}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.maxHeadingLevel="${this.maxHeadingLevel}"
			>
				${this.getSectionHeaders()}
				${this.getA11yList(this.sortProducts(products).map((shoppingProduct) => this.getRow(shoppingProduct)))}
			</kompis-product-list-section>
		`;
    }
    /**
     * @param listItems Content within a list.
     */
    getA11yList(listItems) {
        /* eslint-disable lit-a11y/no-redundant-role */
        return html `
			<ul role="list">
				${listItems}
			</ul>
		`;
        /* eslint-enable lit-a11y/no-redundant-role */
    }
    /**
     * Get sections.
     */
    getSectionStructure() {
        const sections = [];
        for (const section of this.sections ?? []) {
            const shoppingProducts = [];
            for (const shoppingProduct of this.shoppingProducts) {
                const itemId = shoppingProduct.product.itemId;
                if (section.products.includes(itemId)) {
                    shoppingProducts.push(shoppingProduct);
                }
            }
            sections.push({ label: section.label, shoppingProducts });
        }
        return sections;
    }
    /**
     * Sorts products based on chosen sorting option.
     *
     * @param shoppingProducts The shopping products which should be sorted.
     */
    sortProducts(shoppingProducts) {
        if (this.sortBy === SortListOptionsEnum.default) {
            return shoppingProducts;
        }
        const flattened = ProductListSprUtility.flattenSprsOrBundles(shoppingProducts, this.includedProducts ?? []);
        return ProductListSorting.sort(flattened, this.sortBy, this.locale ?? undefined, this.storeAvailabilities ?? undefined);
    }
    /**
     * Returns HTML for a ShoppingProduct row.
     *
     * @param shoppingProduct  Retail item.
     * @returns Template.
     */
    getRow(shoppingProduct) {
        const product = shoppingProduct.product;
        const isBundleProduct = ProductUtility.isBundleProduct(product);
        return html `
			<div class="row-wrapper">
				<kompis-product-row
					@add="${this.onAdd.bind(this, product)}"
					@remove="${this.onRemove.bind(this, product)}"
					@expand="${this.onExpand.bind(this, product)}"
					@collect="${this.onCollect.bind(this)}"
					@quantity="${this.onQuantity.bind(this)}"
					@productImageClick="${(event) => this.dispatchEvent(new CustomEvent('productImageClick', { bubbles: true, detail: { ...event.detail } }))}"
					@productNameClick="${(event) => this.dispatchEvent(new CustomEvent('productNameClick', { bubbles: true, detail: { ...event.detail } }))}"
					.theme="${this.theme}"
					.localizedInformation="${this.localizedInformation}"
					.layout="${this.layout}"
					.translations="${this.translations}"
					.settings="${this.settings}"
					.shoppingProduct="${shoppingProduct}"
					.includedProducts="${this.includedProducts}"
					.storeAvailabilities="${this.storeAvailabilities}"
					.zipAvailabilities="${this.zipAvailabilities}"
					.stores="${this.stores}"
					.storeId="${this.storeId}"
					.productMeasures="${this.productMeasures}"
					.showItemNo="${this.showItemNo}"
					.showStockStatusOnPrint="${this.showStockStatusOnPrint}"
					.editableQuantity="${this.editableQuantity}"
					.showDesignText="${this.showDesignText}"
					.showPackageMeasures="${this.showPackageMeasures}"
					.showWeight="${this.showWeight}"
					.showTotalPrice="${true}"
					.productLink="${this.productLink}"
					.expanded="${this.isExpanded(product)}"
					.collectable="${this.collectable}"
					.collected="${isBundleProduct ? null : this.getCollectedIds(product)}"
					.quantityMinValue="${this.quantityMinValue}"
					.disableLinks="${this.disableLinks}"
					.visibleModal="${!isBundleProduct && product.itemId === this.visibleModal?.itemId
            ? this.visibleModal?.modal
            : null}"
					.optionalState="${this.getProductRowOptionalState(product)}"
					.measurementSystem="${this.measurementSystem}"
					@modalOpen="${(event) => {
            // Bundle products never open modals
            if (isBundleProduct) {
                console.warn('Bundle products should not open modals.');
                return;
            }
            this.dispatchEvent(new CustomEvent('modalOpen', {
                bubbles: true,
                detail: {
                    visibleModal: {
                        modal: event.detail.visibleModal,
                        itemId: product.itemId
                    }
                }
            }));
        }}}"
					@modalClose="${() => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }))}"
				></kompis-product-row>
			</div>
		`;
    }
    getTotalPrice() {
        if (this.layout !== ProductRowLayoutEnum.compactPrint) {
            return null;
        }
        const flattenedShoppingProducts = this.getFlattenedShoppingProducts();
        return html `
			<kompis-product-list-total-price
				.theme="${this.theme}"
				.settings="${this.settings}"
				.translations="${this.translations}"
				.layout="${ProductRowLayoutEnum.compactPrint}"
				.shoppingProducts="${flattenedShoppingProducts}"
			>
			</kompis-product-list-total-price>
		`;
    }
    getFlattenedShoppingProducts() {
        return this.getAddedShoppingProducts(this.shoppingProducts).flatMap((shoppingProduct) => this.flattenNonPIAShoppingProduct(shoppingProduct));
    }
    flattenNonPIAShoppingProduct(shoppingProduct) {
        if (ProductUtility.shouldCalculatePriceFromChildProducts(shoppingProduct.product)) {
            const includedShoppingProducts = ProductUtility.getIncludedShoppingProducts({
                product: shoppingProduct.product,
                includedProducts: this.includedProducts ?? [],
                ignoreInvalidProducts: true
            });
            if (includedShoppingProducts.length > 0 &&
                includedShoppingProducts.length === shoppingProduct.product.content?.child?.length) {
                return includedShoppingProducts.map((sp) => ({
                    ...sp,
                    quantity: sp.quantity * shoppingProduct.quantity
                }));
            }
        }
        return [shoppingProduct];
    }
    /**
     * @param product Product.
     */
    onAdd(product) {
        if (product.content) {
            const removable = (this.removable || []).slice();
            const addable = (this.addable || []).slice();
            const { itemId } = product;
            const index = itemId ? addable.indexOf(itemId) : -1;
            if (index !== -1) {
                addable.splice(index, 1);
            }
            if (itemId) {
                removable.push(itemId);
            }
            this.dispatchEvent(new CustomEvent('add', {
                bubbles: true,
                detail: { removable, addable, itemId }
            }));
        }
    }
    /**
     * @param product Product.
     */
    onRemove(product) {
        if (product.content) {
            const removable = (this.removable || []).slice();
            const addable = (this.addable || []).slice();
            const { itemId } = product;
            const index = itemId ? removable.indexOf(itemId) : -1;
            if (index !== -1) {
                removable.splice(index, 1);
            }
            if (itemId) {
                addable.push(itemId);
            }
            this.dispatchEvent(new CustomEvent('remove', {
                bubbles: true,
                detail: { removable, addable, itemId }
            }));
        }
    }
    /**
     * @param product Product.
     */
    onExpand(product) {
        const expanded = (this.expanded || []).slice();
        const { itemId } = product;
        const index = itemId ? expanded.indexOf(itemId) : -1;
        if (index !== -1) {
            expanded.splice(index, 1);
        }
        else if (itemId) {
            expanded.push(itemId);
        }
        this.dispatchEvent(new CustomEvent('expand', {
            bubbles: true,
            detail: { expanded, itemId }
        }));
    }
    /**
     * @param event Event.
     */
    onCollect(event) {
        let collected = [];
        for (const productRow of Array.from(this.shadowRoot?.querySelectorAll(this.customElements.getInternalName('kompis-product-row')) ?? [])) {
            if (productRow === event.currentTarget) {
                collected = collected.concat(event.detail.collected);
            }
            else if (productRow.collected) {
                collected = collected.concat(productRow.collected);
            }
        }
        this.dispatchEvent(new CustomEvent('collect', {
            bubbles: true,
            detail: { collected, collectId: event.detail.collectId }
        }));
    }
    /**
     * @param event Event.
     */
    onQuantity(event) {
        const changedShoppingProduct = event.detail.shoppingProduct;
        if (!changedShoppingProduct.product.content) {
            return;
        }
        const changedItemId = changedShoppingProduct.product.itemId;
        const changedProductIndex = this.shoppingProducts.findIndex((shoppingProduct) => shoppingProduct.product.itemId === changedItemId);
        const shoppingProducts = [...this.shoppingProducts];
        shoppingProducts[changedProductIndex] = changedShoppingProduct;
        this.dispatchEvent(new CustomEvent('quantity', {
            bubbles: true,
            detail: {
                shoppingProducts,
                shoppingProduct: changedShoppingProduct
            }
        }));
    }
    /**
     * @param product Product.
     * @returns "true" if expanded.
     */
    isExpanded(product) {
        const expanded = this.expanded || [];
        return expanded.includes(product.itemId);
    }
    /**
     * @param product Product.
     * @returns Collected.
     */
    getCollectedIds(product) {
        if (!product.content) {
            return [];
        }
        return (this.collected || []).filter((collectId) => {
            return collectId.startsWith(product.itemId);
        });
    }
    /**
     * @param product Product.
     * @returns Optional state.
     */
    getProductRowOptionalState(product) {
        if (this.removable?.includes(product.itemId)) {
            return ProductRowOptionalStateEnum.removableFromList;
        }
        if (this.addable?.includes(product.itemId)) {
            return ProductRowOptionalStateEnum.addable;
        }
        return ProductRowOptionalStateEnum.mandatory;
    }
}
__decorate([
    property({ type: Object, required: true })
], ProductList.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "includedProducts", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "addable", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "removable", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "sections", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "storeAvailabilities", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "zipAvailabilities", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "settings", void 0);
__decorate([
    property({ type: Object, required: true })
], ProductList.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], ProductList.prototype, "productMeasures", void 0);
__decorate([
    property({ type: String })
], ProductList.prototype, "productLink", void 0);
__decorate([
    property({ type: Boolean })
], ProductList.prototype, "showItemNo", void 0);
__decorate([
    property({ type: Boolean })
], ProductList.prototype, "showWeight", void 0);
__decorate([
    property({ type: Boolean })
], ProductList.prototype, "editableQuantity", void 0);
__decorate([
    property({ type: Boolean })
], ProductList.prototype, "showDesignText", void 0);
__decorate([
    property({ type: Boolean })
], ProductList.prototype, "showPackageMeasures", void 0);
__decorate([
    property({ type: Boolean })
], ProductList.prototype, "showStockStatusOnPrint", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "expanded", void 0);
__decorate([
    property({ type: Boolean })
], ProductList.prototype, "collectable", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "collected", void 0);
__decorate([
    property({ type: Number })
], ProductList.prototype, "quantityMinValue", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "stores", void 0);
__decorate([
    property({ type: String })
], ProductList.prototype, "storeId", void 0);
__decorate([
    property({ type: Boolean })
], ProductList.prototype, "disableLinks", void 0);
__decorate([
    property({ type: String })
], ProductList.prototype, "sortBy", void 0);
__decorate([
    property({ type: String })
], ProductList.prototype, "locale", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "visibleModal", void 0);
__decorate([
    property({ type: String })
], ProductList.prototype, "skipListingLabel", void 0);
__decorate([
    property({ type: Object })
], ProductList.prototype, "maxHeadingLevel", void 0);
__decorate([
    property({ type: String })
], ProductList.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], ProductList.prototype, "measurementSystem", void 0);
//# sourceMappingURL=ProductList.js.map